/**
 * HTTP / future-MCP tool catalog for the attached Curva bridge.
 * Keep descriptions short and intent-oriented for LLM clients.
 */

export interface McpToolDefinition {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
}

const emptyObject = {
  type: "object",
  properties: {},
  additionalProperties: false,
} as const;

const boundsProps = {
  x: { type: "number", description: "X relative to the containing frame (matches the editor inspector). Same as document x when there is no ancestor frame." },
  y: { type: "number", description: "Y relative to the containing frame (matches the editor inspector). Same as document y when there is no ancestor frame." },
  width: { type: "number" },
  height: { type: "number" },
} as const;

const gradientPointSchema = {
  type: "object",
  properties: {
    x: { type: "number" },
    y: { type: "number" },
  },
  required: ["x", "y"],
  additionalProperties: false,
} as const;

const effectSchema = {
  oneOf: [
    {
      type: "object",
      properties: {
        type: { type: "string", enum: ["dropShadow", "innerShadow"] },
        visible: { type: "boolean" },
        offsetX: { type: "number" },
        offsetY: { type: "number" },
        blur: { type: "number", minimum: 0 },
        spread: { type: "number" },
        color: { type: "string" },
      },
      required: ["type", "color"],
      additionalProperties: false,
    },
    {
      type: "object",
      properties: {
        type: { type: "string", enum: ["layerBlur"] },
        visible: { type: "boolean" },
        mode: { type: "string", enum: ["uniform", "progressive"] },
        blur: { type: "number", minimum: 0 },
        start: { type: "number", minimum: 0 },
        end: { type: "number", minimum: 0 },
      },
      required: ["type", "blur"],
      additionalProperties: false,
    },
    {
      type: "object",
      properties: {
        type: { type: "string", enum: ["backgroundBlur"] },
        visible: { type: "boolean" },
        blur: { type: "number", minimum: 0 },
      },
      required: ["type", "blur"],
      additionalProperties: false,
    },
  ],
} as const;

/** Shared typography fields for create_text / set_typography / update_style. */
const typographyProps = {
  fontFamily: { type: "string" },
  fontWeight: { type: "number" },
  fontSize: { type: "number" },
  letterSpacing: { type: "number" },
  horizontalAlign: { type: "string", enum: ["LEFT", "CENTER", "RIGHT", "JUSTIFIED"] },
  verticalAlign: { type: "string", enum: ["TOP", "CENTER", "BOTTOM"] },
  lineHeight: {
    oneOf: [
      { type: "string", enum: ["auto"] },
      {
        type: "object",
        properties: {
          unit: { type: "string", enum: ["px", "percent"] },
          value: { type: "number" },
        },
        required: ["unit", "value"],
        additionalProperties: false,
      },
    ],
  },
} as const;

export const MCP_TOOL_DEFINITIONS: McpToolDefinition[] = [
  // ── Session ────────────────────────────────────────────────────────────
  {
    name: "undo",
    description: "Undo the last document edit in the attached Curva editor session.",
    inputSchema: emptyObject,
  },
  {
    name: "redo",
    description: "Redo the last undone document edit in the attached Curva editor session.",
    inputSchema: emptyObject,
  },
  {
    name: "get_document_json",
    description: "Return the full live .curva document JSON from the attached editor.",
    inputSchema: emptyObject,
  },
  {
    name: "batch_tools",
    description:
      "Run multiple Curva tools sequentially in one round trip. Stops on first failure; earlier successes remain applied. Nested batch_tools is not allowed.",
    inputSchema: {
      type: "object",
      properties: {
        calls: {
          type: "array",
          minItems: 1,
          maxItems: 20,
          items: {
            type: "object",
            properties: {
              name: { type: "string" },
              arguments: { type: "object" },
            },
            required: ["name"],
            additionalProperties: false,
          },
        },
      },
      required: ["calls"],
      additionalProperties: false,
    },
  },
  {
    name: "get_docs",
    description:
      "Read Curva agent guides (overview, components, layout, svg, styles, scripts). Omit topic to list available guides. Call before creating components — variants are nested children, not Figma slash names. Prefer document styles for shared color tokens.",
    inputSchema: {
      type: "object",
      properties: {
        topic: { type: "string", enum: ["overview", "components", "layout", "svg", "styles", "scripts"] },
      },
      additionalProperties: false,
    },
  },

  // ── Query ──────────────────────────────────────────────────────────────
  {
    name: "get_document_summary",
    description: "High-level overview: pages, top-level nodes, counts by type, selection.",
    inputSchema: emptyObject,
  },
  {
    name: "list_pages",
    description: "List pages in the live document (id, name, child count).",
    inputSchema: emptyObject,
  },
  {
    name: "get_selection",
    description: "Return the current editor selection (page, selected node ids, mode).",
    inputSchema: emptyObject,
  },
  {
    name: "select_nodes",
    description: "Set the editor selection to the given node ids.",
    inputSchema: {
      type: "object",
      properties: {
        nodeIds: { type: "array", items: { type: "string" } },
        primaryNodeId: { type: "string" },
      },
      required: ["nodeIds"],
      additionalProperties: false,
    },
  },
  {
    name: "get_node",
    description: "Return a compact summary of a scene node by id.",
    inputSchema: {
      type: "object",
      properties: { nodeId: { type: "string" } },
      required: ["nodeId"],
      additionalProperties: false,
    },
  },
  {
    name: "list_children",
    description: "List direct children of a page or container node.",
    inputSchema: {
      type: "object",
      properties: { parentId: { type: "string" } },
      required: ["parentId"],
      additionalProperties: false,
    },
  },
  {
    name: "find_nodes",
    description: "Find scene nodes by optional name substring and/or type.",
    inputSchema: {
      type: "object",
      properties: {
        name: { type: "string" },
        type: { type: "string" },
        limit: { type: "number" },
      },
      additionalProperties: false,
    },
  },

  // ── Create ─────────────────────────────────────────────────────────────
  {
    name: "create_rectangle",
    description: "Create a rectangle primitive. Optional parentId (page or frame); defaults to active page. x,y are relative to the containing frame (like the editor inspector).",
    inputSchema: {
      type: "object",
      properties: { ...boundsProps, parentId: { type: "string" }, name: { type: "string" }, fill: { type: "string" } },
      required: ["x", "y", "width", "height"],
      additionalProperties: false,
    },
  },
  {
    name: "create_oval",
    description: "Create an oval primitive. x,y are relative to the containing frame (like the editor inspector).",
    inputSchema: {
      type: "object",
      properties: { ...boundsProps, parentId: { type: "string" }, name: { type: "string" }, fill: { type: "string" } },
      required: ["x", "y", "width", "height"],
      additionalProperties: false,
    },
  },
  {
    name: "create_line",
    description: "Create a line primitive from bounds (x,y → x+width,y+height). x,y are relative to the containing frame (like the editor inspector).",
    inputSchema: {
      type: "object",
      properties: { ...boundsProps, parentId: { type: "string" }, name: { type: "string" } },
      required: ["x", "y", "width", "height"],
      additionalProperties: false,
    },
  },
  {
    name: "create_frame",
    description: "Create a frame. Optional name and parentId. x,y are relative to the containing frame when nested (like the editor inspector).",
    inputSchema: {
      type: "object",
      properties: { ...boundsProps, parentId: { type: "string" }, name: { type: "string" } },
      required: ["x", "y", "width", "height"],
      additionalProperties: false,
    },
  },
  {
    name: "create_text",
    description:
      "Create a text node. Provide characters; optional bounds (otherwise auto-sized at x,y) and typography (fontFamily, weight, size, etc.). x,y are relative to the containing frame (like the editor inspector).",
    inputSchema: {
      type: "object",
      properties: {
        x: { type: "number" },
        y: { type: "number" },
        width: { type: "number" },
        height: { type: "number" },
        characters: { type: "string" },
        parentId: { type: "string" },
        name: { type: "string" },
        fill: { type: "string" },
        ...typographyProps,
      },
      required: ["x", "y", "characters"],
      additionalProperties: false,
    },
  },
  {
    name: "import_svg",
    description:
      "Import SVG markup as native editable Curva nodes at top-left x,y (relative to the containing frame, like the editor inspector). Useful for icons and compound vector artwork. Optional parentId defaults to the active page; non-fatal import notes are returned. See get_docs topic=svg.",
    inputSchema: {
      type: "object",
      properties: {
        svg: { type: "string", minLength: 1, maxLength: 1_000_000 },
        x: { type: "number" },
        y: { type: "number" },
        parentId: { type: "string" },
        name: { type: "string" },
      },
      required: ["svg", "x", "y"],
      additionalProperties: false,
    },
  },

  // ── Style ──────────────────────────────────────────────────────────────
  {
    name: "set_fill",
    description:
      "Set the solid fill color on one or more nodes (CSS color string). Detaches any bound fill style first so the local paint is visible. Prefer create_style/apply_style/update_style for shared tokens.",
    inputSchema: {
      type: "object",
      properties: {
        nodeIds: { type: "array", items: { type: "string" } },
        color: { type: "string" },
      },
      required: ["nodeIds", "color"],
      additionalProperties: false,
    },
  },
  {
    name: "set_gradient_fill",
    description:
      "Replace node fills with a linear or radial gradient. Points use normalized node coordinates. Detaches any bound fill style first.",
    inputSchema: {
      type: "object",
      properties: {
        nodeIds: { type: "array", items: { type: "string" } },
        type: { type: "string", enum: ["linear", "radial"] },
        stops: {
          type: "array",
          minItems: 2,
          items: {
            type: "object",
            properties: {
              position: { type: "number", minimum: 0, maximum: 1 },
              color: { type: "string" },
            },
            required: ["position", "color"],
            additionalProperties: false,
          },
        },
        colorSpace: { type: "string", enum: ["rgb", "oklch"] },
        angle: { type: "number" },
        start: gradientPointSchema,
        end: gradientPointSchema,
        center: gradientPointSchema,
        radiusPoint: gradientPointSchema,
        radius: { type: "number", exclusiveMinimum: 0 },
      },
      required: ["nodeIds", "type", "stops"],
      additionalProperties: false,
    },
  },
  {
    name: "set_effects",
    description:
      "Replace effects on nodes. Supports shadows, layer blur, background blur; pass [] to clear. Detaches any bound effects style first.",
    inputSchema: {
      type: "object",
      properties: {
        nodeIds: { type: "array", items: { type: "string" } },
        effects: { type: "array", items: effectSchema },
      },
      required: ["nodeIds", "effects"],
      additionalProperties: false,
    },
  },
  {
    name: "set_stroke",
    description: "Set stroke color, optional width, and optional align on nodes. Align defaults to \"inside\" (the editor default). Detaches any bound stroke style first.",
    inputSchema: {
      type: "object",
      properties: {
        nodeIds: { type: "array", items: { type: "string" } },
        color: { type: "string" },
        width: { type: "number" },
        align: { type: "string", enum: ["inside", "center", "outside"] },
      },
      required: ["nodeIds", "color"],
      additionalProperties: false,
    },
  },
  {
    name: "set_opacity",
    description: "Set node opacity (0–1).",
    inputSchema: {
      type: "object",
      properties: {
        nodeIds: { type: "array", items: { type: "string" } },
        opacity: { type: "number" },
      },
      required: ["nodeIds", "opacity"],
      additionalProperties: false,
    },
  },
  {
    name: "set_corner_radius",
    description: "Set corner radius on frames or rounded rectangles. Pass radius or radii[tl,tr,br,bl].",
    inputSchema: {
      type: "object",
      properties: {
        nodeIds: { type: "array", items: { type: "string" } },
        radius: { type: "number" },
        radii: { type: "array", items: { type: "number" }, minItems: 4, maxItems: 4 },
      },
      required: ["nodeIds"],
      additionalProperties: false,
    },
  },
  {
    name: "set_text",
    description: "Replace text characters on a text node.",
    inputSchema: {
      type: "object",
      properties: {
        nodeId: { type: "string" },
        characters: { type: "string" },
      },
      required: ["nodeId", "characters"],
      additionalProperties: false,
    },
  },
  {
    name: "set_typography",
    description:
      "Patch local typography on text nodes (fontFamily, weight, size, tracking, align, lineHeight). Detaches any bound text style first so the change is visible. Prefer this for one-off font changes; use create_style/apply_style for shared text tokens. Font loading is async — wait briefly before export_node_png when switching families.",
    inputSchema: {
      type: "object",
      properties: {
        nodeIds: { type: "array", items: { type: "string" }, minItems: 1 },
        ...typographyProps,
      },
      required: ["nodeIds"],
      additionalProperties: false,
    },
  },
  {
    name: "set_text_layout",
    description:
      "Set text layout mode on text nodes: AUTO_WIDTH (grow with content), AUTO_HEIGHT (fixed width, wrap), or FIXED_SIZE.",
    inputSchema: {
      type: "object",
      properties: {
        nodeIds: { type: "array", items: { type: "string" }, minItems: 1 },
        textLayout: { type: "string", enum: ["AUTO_WIDTH", "AUTO_HEIGHT", "FIXED_SIZE"] },
      },
      required: ["nodeIds", "textLayout"],
      additionalProperties: false,
    },
  },
  {
    name: "list_fonts",
    description:
      "Search the Google font catalog available in Curva. Optional query filters by family name substring. Use before set_typography when picking a family (e.g. Poppins).",
    inputSchema: {
      type: "object",
      properties: {
        query: { type: "string" },
        limit: { type: "number", minimum: 1, maximum: 200 },
      },
      additionalProperties: false,
    },
  },

  // ── Document styles (shared tokens) ────────────────────────────────────
  {
    name: "list_styles",
    description:
      "List shared document styles (fill / stroke / text / effects). Optional kind filter. See get_docs topic=styles.",
    inputSchema: {
      type: "object",
      properties: {
        kind: { type: "string", enum: ["fill", "stroke", "text", "effects"] },
      },
      additionalProperties: false,
    },
  },
  {
    name: "get_style",
    description: "Get one document style by id, including its paint/typography/effects payload and usage count.",
    inputSchema: {
      type: "object",
      properties: { styleId: { type: "string" } },
      required: ["styleId"],
      additionalProperties: false,
    },
  },
  {
    name: "create_style",
    description:
      "Create a shared document style from a node's current local values and bind it to that node. kind: fill | stroke | text | effects. Prefer this over duplicating raw colors across screens.",
    inputSchema: {
      type: "object",
      properties: {
        nodeId: { type: "string" },
        kind: { type: "string", enum: ["fill", "stroke", "text", "effects"] },
        name: { type: "string" },
      },
      required: ["nodeId", "kind", "name"],
      additionalProperties: false,
    },
  },
  {
    name: "apply_style",
    description: "Bind an existing document style to one or more nodes (by styleId). Kind is taken from the style.",
    inputSchema: {
      type: "object",
      properties: {
        nodeIds: { type: "array", items: { type: "string" }, minItems: 1 },
        styleId: { type: "string" },
      },
      required: ["nodeIds", "styleId"],
      additionalProperties: false,
    },
  },
  {
    name: "detach_style",
    description:
      "Detach a style binding from nodes and inline the resolved values as local paints/typography/effects.",
    inputSchema: {
      type: "object",
      properties: {
        nodeIds: { type: "array", items: { type: "string" }, minItems: 1 },
        kind: { type: "string", enum: ["fill", "stroke", "text", "effects"] },
      },
      required: ["nodeIds", "kind"],
      additionalProperties: false,
    },
  },
  {
    name: "update_style",
    description:
      "Update a shared document style. All nodes bound to it update. Pass name and/or fromNodeId (re-snapshot) and/or kind-specific fields (color for fill/stroke; width/align for stroke; effects[]; typography for text).",
    inputSchema: {
      type: "object",
      properties: {
        styleId: { type: "string" },
        name: { type: "string" },
        fromNodeId: { type: "string" },
        color: { type: "string" },
        width: { type: "number" },
        align: { type: "string", enum: ["inside", "center", "outside"] },
        effects: { type: "array", items: effectSchema },
        typography: {
          type: "object",
          properties: { ...typographyProps },
          additionalProperties: false,
        },
      },
      required: ["styleId"],
      additionalProperties: false,
    },
  },
  {
    name: "delete_style",
    description:
      "Delete a document style. Bound nodes are detached first (resolved values inlined), then the library entry is removed.",
    inputSchema: {
      type: "object",
      properties: { styleId: { type: "string" } },
      required: ["styleId"],
      additionalProperties: false,
    },
  },

  // ── Transform ──────────────────────────────────────────────────────────
  {
    name: "set_bounds",
    description: "Set a node's bounds. x,y are relative to the containing frame (matches the editor inspector and the values returned by get_node).",
    inputSchema: {
      type: "object",
      properties: {
        nodeId: { type: "string" },
        ...boundsProps,
      },
      required: ["nodeId", "x", "y", "width", "height"],
      additionalProperties: false,
    },
  },
  {
    name: "set_rotation",
    description: "Set node rotation in degrees.",
    inputSchema: {
      type: "object",
      properties: {
        nodeId: { type: "string" },
        rotation: { type: "number" },
      },
      required: ["nodeId", "rotation"],
      additionalProperties: false,
    },
  },
  {
    name: "rename_node",
    description: "Rename a scene node.",
    inputSchema: {
      type: "object",
      properties: {
        nodeId: { type: "string" },
        name: { type: "string" },
      },
      required: ["nodeId", "name"],
      additionalProperties: false,
    },
  },
  {
    name: "delete_nodes",
    description: "Delete nodes by id.",
    inputSchema: {
      type: "object",
      properties: {
        nodeIds: { type: "array", items: { type: "string" } },
      },
      required: ["nodeIds"],
      additionalProperties: false,
    },
  },
  {
    name: "reparent_nodes",
    description: "Move nodes under a new parent (page or container). Optional index.",
    inputSchema: {
      type: "object",
      properties: {
        nodeIds: { type: "array", items: { type: "string" } },
        parentId: { type: "string" },
        index: { type: "number" },
      },
      required: ["nodeIds", "parentId"],
      additionalProperties: false,
    },
  },

  // ── Structure ──────────────────────────────────────────────────────────
  {
    name: "group_nodes",
    description: "Group the given nodes (or current selection if nodeIds omitted).",
    inputSchema: {
      type: "object",
      properties: { nodeIds: { type: "array", items: { type: "string" } } },
      additionalProperties: false,
    },
  },
  {
    name: "frame_nodes",
    description: "Wrap nodes in a frame (or current selection if nodeIds omitted).",
    inputSchema: {
      type: "object",
      properties: { nodeIds: { type: "array", items: { type: "string" } } },
      additionalProperties: false,
    },
  },
  {
    name: "ungroup_nodes",
    description: "Ungroup the given group/frame nodes (or current selection).",
    inputSchema: {
      type: "object",
      properties: { nodeIds: { type: "array", items: { type: "string" } } },
      additionalProperties: false,
    },
  },
  {
    name: "reorder_nodes",
    description: "Change layer order: front | back | forward | backward.",
    inputSchema: {
      type: "object",
      properties: {
        nodeIds: { type: "array", items: { type: "string" } },
        placement: { type: "string", enum: ["front", "back", "forward", "backward"] },
      },
      required: ["placement"],
      additionalProperties: false,
    },
  },
  {
    name: "align_nodes",
    description: "Align selected/given nodes: left | horizontalCenter | right | top | verticalCenter | bottom.",
    inputSchema: {
      type: "object",
      properties: {
        nodeIds: { type: "array", items: { type: "string" } },
        action: {
          type: "string",
          enum: ["left", "horizontalCenter", "right", "top", "verticalCenter", "bottom"],
        },
      },
      required: ["action"],
      additionalProperties: false,
    },
  },

  // ── Layout ─────────────────────────────────────────────────────────────
  {
    name: "set_auto_layout",
    description: "Configure frame auto-layout. mode: off | flex | repeat | mirror. Optional flex/repeat/mirror settings.",
    inputSchema: {
      type: "object",
      properties: {
        nodeId: { type: "string" },
        mode: { type: "string", enum: ["off", "flex", "repeat", "mirror"] },
        direction: { type: "string", enum: ["row", "row-reverse", "column", "column-reverse"] },
        gap: { type: "number" },
        padding: { type: "number" },
        justifyContent: { type: "string" },
        alignItems: { type: "string" },
        widthSizing: { type: "string", enum: ["fixed", "hug"] },
        heightSizing: { type: "string", enum: ["fixed", "hug"] },
        repeatMode: { type: "string", enum: ["row", "column", "grid", "radial"] },
        count: { type: "number" },
        columns: { type: "number" },
        rows: { type: "number" },
        columnGap: { type: "number" },
        rowGap: { type: "number" },
        radius: { type: "number" },
        startAngle: { type: "number" },
        mirrorAxis: { type: "string", enum: ["horizontal", "vertical", "both"] },
      },
      required: ["nodeId", "mode"],
      additionalProperties: false,
    },
  },
  {
    name: "set_constraints",
    description: "Set autoresizing constraints on a node (horizontal/vertical: min | max | center | stretch | scale).",
    inputSchema: {
      type: "object",
      properties: {
        nodeId: { type: "string" },
        horizontal: { type: "string" },
        vertical: { type: "string" },
      },
      required: ["nodeId"],
      additionalProperties: false,
    },
  },

  // ── Boolean / vectorize ────────────────────────────────────────────────
  {
    name: "boolean_operation",
    description: "Boolean-combine current selection (or nodeIds). operation: union | subtract | intersect | exclude.",
    inputSchema: {
      type: "object",
      properties: {
        nodeIds: { type: "array", items: { type: "string" } },
        operation: { type: "string", enum: ["union", "subtract", "intersect", "exclude"] },
      },
      required: ["operation"],
      additionalProperties: false,
    },
  },
  {
    name: "flatten_nodes",
    description: "Flatten selection/nodes into a vector network.",
    inputSchema: {
      type: "object",
      properties: { nodeIds: { type: "array", items: { type: "string" } } },
      additionalProperties: false,
    },
  },
  {
    name: "outline_stroke",
    description: "Convert strokes on selection/nodes to outlined vectors.",
    inputSchema: {
      type: "object",
      properties: { nodeIds: { type: "array", items: { type: "string" } } },
      additionalProperties: false,
    },
  },

  // ── Components ─────────────────────────────────────────────────────────
  {
    name: "list_components",
    description:
      "List all document components with nested variant ids/names and instance counts. Optional name substring filter. Prefer this before insert_instance — variants are nested children, not Figma slash names. See get_docs topic=components.",
    inputSchema: {
      type: "object",
      properties: {
        name: { type: "string" },
      },
      additionalProperties: false,
    },
  },
  {
    name: "create_component",
    description:
      "Create a component from the given nodes (or current selection). Produces one initial nested variant — not Figma slash-name variants. Prefer keeping the component outside the main design frame. See get_docs topic=components.",
    inputSchema: {
      type: "object",
      properties: { nodeIds: { type: "array", items: { type: "string" } } },
      additionalProperties: false,
    },
  },
  {
    name: "add_component_variant",
    description:
      "Duplicate the last nested variant under a component (new componentVariant child). Rename/restyle the new variant afterward. Not Figma slash names — variants are nested children. See get_docs topic=components.",
    inputSchema: {
      type: "object",
      properties: { componentId: { type: "string" } },
      required: ["componentId"],
      additionalProperties: false,
    },
  },
  {
    name: "insert_instance",
    description:
      "Insert an instance of a component variant. Prefer parentId + frame-relative x,y (same as create_*). Without parentId, x,y are a document-space drop point that picks the deepest frame under the cursor and centers the instance on that point. Pass variantId from list_components. See get_docs topic=components.",
    inputSchema: {
      type: "object",
      properties: {
        componentId: { type: "string" },
        variantId: { type: "string" },
        parentId: { type: "string" },
        x: { type: "number" },
        y: { type: "number" },
      },
      required: ["componentId", "x", "y"],
      additionalProperties: false,
    },
  },
  {
    name: "set_instance_variant",
    description:
      "Swap which componentVariant an instance displays. Clears per-instance overrides (editor behavior). Prefer this over delete + insert_instance. See get_docs topic=components.",
    inputSchema: {
      type: "object",
      properties: {
        instanceId: { type: "string" },
        variantId: { type: "string" },
      },
      required: ["instanceId", "variantId"],
      additionalProperties: false,
    },
  },
  {
    name: "duplicate_nodes",
    description:
      "Duplicate nodes (or the current selection) in place with an optional offset. Returns the new node ids. Prefer this over re-inserting the same component many times.",
    inputSchema: {
      type: "object",
      properties: {
        nodeIds: { type: "array", items: { type: "string" } },
        offsetX: { type: "number" },
        offsetY: { type: "number" },
      },
      additionalProperties: false,
    },
  },
  {
    name: "detach_instance",
    description: "Detach a component instance into a regular group.",
    inputSchema: {
      type: "object",
      properties: { instanceId: { type: "string" } },
      required: ["instanceId"],
      additionalProperties: false,
    },
  },

  // ── Scripts ────────────────────────────────────────────────────────────
  {
    name: "list_scripts",
    description:
      "List document Scripts library entries (scripts + folders). Scripts are document-level Luau; frames attach via scriptId. See get_docs topic=scripts.",
    inputSchema: emptyObject,
  },
  {
    name: "get_script",
    description:
      "Get one script by id: full source, parsed @param controls, and attached frames. Prefer this before run_script when the script has params.",
    inputSchema: {
      type: "object",
      properties: { scriptId: { type: "string" } },
      required: ["scriptId"],
      additionalProperties: false,
    },
  },
  {
    name: "create_script",
    description:
      "Create a document script (Luau). Optional name, source, and parentFolderId. Default source is a minimal init() stub.",
    inputSchema: {
      type: "object",
      properties: {
        name: { type: "string" },
        source: { type: "string" },
        parentFolderId: { type: "string" },
      },
      additionalProperties: false,
    },
  },
  {
    name: "update_script",
    description:
      "Rename and/or replace a script's source. Does not mutate source when running with @param values — only this tool (or the editor) edits stored source.",
    inputSchema: {
      type: "object",
      properties: {
        scriptId: { type: "string" },
        name: { type: "string" },
        source: { type: "string" },
      },
      required: ["scriptId"],
      additionalProperties: false,
    },
  },
  {
    name: "delete_script",
    description:
      "Delete a script or script folder by id (scriptId accepts either). Deleting a folder removes nested entries.",
    inputSchema: {
      type: "object",
      properties: { scriptId: { type: "string" } },
      required: ["scriptId"],
      additionalProperties: false,
    },
  },
  {
    name: "duplicate_script",
    description: "Duplicate a script in the library. Returns the new script.",
    inputSchema: {
      type: "object",
      properties: { scriptId: { type: "string" } },
      required: ["scriptId"],
      additionalProperties: false,
    },
  },
  {
    name: "create_script_folder",
    description: "Create a folder in the Scripts library for organizing scripts.",
    inputSchema: {
      type: "object",
      properties: { name: { type: "string" } },
      additionalProperties: false,
    },
  },
  {
    name: "move_script",
    description:
      "Move a script or folder within the library. parentFolderId omitted = library root. index is the insertion index among siblings.",
    inputSchema: {
      type: "object",
      properties: {
        scriptId: { type: "string" },
        parentFolderId: { type: "string" },
        index: { type: "number" },
      },
      required: ["scriptId"],
      additionalProperties: false,
    },
  },
  {
    name: "attach_script",
    description:
      "Attach a library script to a frame (sets frame.scriptId). One script can be shared by many frames. Running still needs a context frame.",
    inputSchema: {
      type: "object",
      properties: {
        frameId: { type: "string" },
        scriptId: { type: "string" },
      },
      required: ["frameId", "scriptId"],
      additionalProperties: false,
    },
  },
  {
    name: "detach_script",
    description: "Clear a frame's attached script (scriptId → undefined).",
    inputSchema: {
      type: "object",
      properties: { frameId: { type: "string" } },
      required: ["frameId"],
      additionalProperties: false,
    },
  },
  {
    name: "run_script",
    description:
      "Run a document script against a context frame (Luau `frame` global). Optional contextFrameId; otherwise uses the editor selection, falling back to the top-level frame under the viewport center (same as paste). Optional values object supplies @param overrides for this run only (defaults used for omitted keys) — never opens the pre-run UI and never mutates stored source. Requires Allow scripts. See get_docs topic=scripts.",
    inputSchema: {
      type: "object",
      properties: {
        scriptId: { type: "string" },
        contextFrameId: { type: "string" },
        values: {
          type: "object",
          additionalProperties: {
            anyOf: [{ type: "number" }, { type: "string" }, { type: "boolean" }],
          },
        },
      },
      required: ["scriptId"],
      additionalProperties: false,
    },
  },

  // ── Feedback ───────────────────────────────────────────────────────────
  {
    name: "export_node_png",
    description: "Rasterize a node to PNG and return a base64 data URL.",
    inputSchema: {
      type: "object",
      properties: {
        nodeId: { type: "string" },
        scale: { type: "number" },
      },
      required: ["nodeId"],
      additionalProperties: false,
    },
  },
  {
    name: "export_selection_png",
    description: "Rasterize the primary selected node (or nodeId) to PNG base64 data URL.",
    inputSchema: {
      type: "object",
      properties: {
        nodeId: { type: "string" },
        scale: { type: "number" },
      },
      additionalProperties: false,
    },
  },
];

export function mcpToolDefinition(name: string): McpToolDefinition | undefined {
  return MCP_TOOL_DEFINITIONS.find(tool => tool.name === name);
}
